import type { ApiClientV2Default } from 'utils/api-client/api-client.utils'
import { FromSwagger, Nullable } from 'types/helpers'

import { AddressAttributes, NaturalPersonAttributes, VerificationSessionAttributes } from 'types/__generated__/api'

export type NaturalPersonResponse = FromSwagger<NaturalPersonAttributes>
export type AddressResponse = FromSwagger<AddressAttributes>
export type NaturalPersonSessionResponse = FromSwagger<VerificationSessionAttributes> & {
  documentsRequested?: boolean
  draftLegalEntityData: {
    addressAttributes?: AddressResponse
    vatCategory?: Nullable<string>
  }
}
export type NaturalPersonWithAddressResponse = NaturalPersonResponse & {
  attributes: AddressResponse
}

const NATURAL_PERSONS_URL = '/profile_journey/natural_persons'

export interface PersonalReqData {
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: Nullable<string>
  phone: string | null
  salutation: Nullable<string>
  nationality: string
}

export interface AddressReqData {
  line1: string
  line2: string
  city: string
  postal_code: string // should be snake_case
  country_code: string // should be snake_case
  state?: string
}

export interface NaturalPersonEntityReqData {
  firstName: string
  lastName: string
  address?: AddressReqData
}

export interface TaxReqData {
  taxNumber?: Nullable<string>
  vatNumber: Nullable<string>
  vatCategory: string
  vatExemptReason: string
  tradingName: Nullable<string>
  taxIdentificationNumber?: Nullable<string>
  euWideThreshold: boolean
}

const buildDataObject = (type: string, attributes: object = {}, id?: string) => ({
  ...(id && { id }),
  type,
  attributes,
})

export const createNaturalPersonsEntityApi = ({ POST, PATCH, GET }: ApiClientV2Default) => ({
  createEntity: (data?: NaturalPersonEntityReqData) =>
    POST<NaturalPersonResponse>(NATURAL_PERSONS_URL, {
      data: buildDataObject('natural_persons', data),
    }),
  updateEntity: (id: string, data: NaturalPersonEntityReqData) =>
    PATCH<NaturalPersonResponse>(`${NATURAL_PERSONS_URL}/${id}`, {
      data: buildDataObject('natural_persons', data, id),
    }),
  fetchEntity: (id: string) =>
    GET<NaturalPersonWithAddressResponse>(`${NATURAL_PERSONS_URL}/${id}`, {
      include: ['address'],
    }),
  updatePersonal: (id: string, data: PersonalReqData) =>
    PATCH<NaturalPersonResponse>(`${NATURAL_PERSONS_URL}/${id}/personal`, {
      data: buildDataObject('natural_persons', data),
    }),
  updateAddress: (id: string, data: AddressReqData) =>
    PATCH<NaturalPersonResponse>(`${NATURAL_PERSONS_URL}/${id}/address`, {
      data: buildDataObject('natural_persons', data),
    }),
  updateTax: (id: string, data: TaxReqData) =>
    PATCH<NaturalPersonResponse>(`${NATURAL_PERSONS_URL}/${id}/tax`, {
      data: buildDataObject('natural_persons', data),
    }),
  getCurrentSession: (id: string) =>
    GET<NaturalPersonSessionResponse>(`${NATURAL_PERSONS_URL}/${id}/verification_sessions/current`),
  createSession: (id: string) =>
    POST<NaturalPersonSessionResponse>(`${NATURAL_PERSONS_URL}/${id}/verification_sessions`),
})

export type NaturalPersonsEntityApi = ReturnType<typeof createNaturalPersonsEntityApi>
